import { useTranslation } from 'react-i18next';
import { useAmazonConnectAgent } from '@/hooks/useAmazonConnectAgent';
import { useAmazonConnectContacts } from '@/hooks/useAmazonConnectContacts';
import { ActiveCallBanner } from './ActiveCallBanner';
import {
  getActiveConnection,
  getInitialOrCaptureContactId,
  hasActiveCallLeg,
} from './contactUtils';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import { useAppSelector } from '@/store/hooks';
import type { ConnectContact } from '@/types';
import { Paper, Typography } from '@gapartners/ui';

function endContact(contact: ConnectContact) {
  try {
    const connection = getActiveConnection(contact);
    if (connection) {
      connection.destroy({ success: () => {}, failure: () => {} });
    } else {
      try {
        if (!contact.isConnected()) {
          contact.clear({ success: () => {}, failure: () => {} });
        }
      } catch {
        // contact may already be destroyed (e.g. remote hung up)
      }
    }
  } catch {
    // contact/connection no longer exists (e.g. StateError)
  }
}

/**
 * Shows banner with mute, hold, and hang up when there is an active voice contact;
 * for active chat, shows "Active Chat" and End (chat messages live in Live Transcript).
 * Ending the contact does not clear transcripts/case — that happens when user clicks "Finish After Call Work".
 */
export function ActiveCallBannerContainer() {
  const { t } = useTranslation();
  const { agent, muted } = useAmazonConnectAgent();
  const { activeContacts } = useAmazonConnectContacts();
  const focusedContactId = useAppSelector(state => state.call.focusedContactId);

  const activeConnectedContact =
    activeContacts.find(contact => {
      if (!hasActiveCallLeg(contact)) return false;
      if (!focusedContactId) return false;
      try {
        return (
          contact.getContactId() === focusedContactId ||
          getInitialOrCaptureContactId(contact) === focusedContactId
        );
      } catch {
        return false;
      }
    }) ?? null;
  const activeIsChat =
    activeConnectedContact != null &&
    connectAdapter.isChatContact(activeConnectedContact);

  const handleEndCall = () => {
    if (activeConnectedContact) {
      endContact(activeConnectedContact);
      // Do not clear call context here — transcripts and case details stay visible
      // until user clicks "Finish After Call Work" (handled in AmazonConnectControls).
    }
  };

  if (activeConnectedContact) {
    return (
      <ActiveCallBanner
        contact={activeConnectedContact}
        agent={agent}
        muted={muted}
        isChat={activeIsChat}
        onEndCall={handleEndCall}
      />
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography
        variant="h6"
        fontWeight={700}
        color="text.primary"
        sx={{ mb: 1, fontSize: '1.125rem' }}
      >
        {t('components.amazonConnect.activeCall')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('components.contactCenter.noActiveCall')}
      </Typography>
    </Paper>
  );
}
